export type FeaturedImageElement = {
    decode(): Promise<void>;
    remove(): void;
    removeAttribute(name: string): void;
    src: string;
};

export type FeaturedImageLoadOperation = {
    readonly image: FeaturedImageElement;
    readonly signal: AbortSignal;
    readonly result: Promise<void>;
    cancel(reason?: unknown): void;
};

export class FeaturedImageLoadOwner {
    readonly #operations = new Set<FeaturedImageLoadOperation>();

    get pendingCount(): number {
        return this.#operations.size;
    }

    start(image: FeaturedImageElement, source: string, signal: AbortSignal): FeaturedImageLoadOperation {
        let resolveResult: () => void;
        let rejectResult: (reason: unknown) => void;
        const result = new Promise<void>((resolve, reject) => {
            resolveResult = resolve;
            rejectResult = reject;
        });
        let settled = false;
        let onAbort: () => void;
        const operation: FeaturedImageLoadOperation = {
            image,
            signal,
            result,
            cancel: reason => settle(reason ?? createAbortError(), true)
        };
        const settle = (error: unknown, releaseSource: boolean): void => {
            if (settled) return;
            settled = true;
            signal.removeEventListener('abort', onAbort);
            this.#operations.delete(operation);
            if (releaseSource) releaseFeaturedImageSource(image);
            if (error == null) resolveResult();
            else rejectResult(error);
        };
        onAbort = () => operation.cancel(signal.reason);

        this.#operations.add(operation);
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted) {
            onAbort();
            return operation;
        }

        try {
            image.src = source;
            image.decode().then(
                () => settle(null, false),
                error => settle(error, true));
        } catch (error) {
            settle(error, true);
        }
        return operation;
    }

    cancelAll(reason: unknown = createAbortError()): void {
        for (const operation of [...this.#operations]) operation.cancel(reason);
    }
}

export class FeaturedImageLayerOwner {
    readonly #images = new Set<FeaturedImageElement>();

    get count(): number {
        return this.#images.size;
    }

    own<T extends FeaturedImageElement>(image: T): T {
        this.#images.add(image);
        return image;
    }

    owns(image: FeaturedImageElement): boolean {
        return this.#images.has(image);
    }

    dispose(image: FeaturedImageElement): void {
        if (!this.#images.delete(image)) return;
        disposeFeaturedImage(image);
    }

    disposeAll(): void {
        for (const image of [...this.#images]) this.dispose(image);
    }
}

export function releaseFeaturedImageSource(image: FeaturedImageElement): void {
    image.removeAttribute('src');
    image.removeAttribute('srcset');
}

export function disposeFeaturedImage(image: FeaturedImageElement): void {
    releaseFeaturedImageSource(image);
    image.remove();
}

export function isFeaturedImageLoadCancellation(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

function createAbortError(): DOMException {
    return new DOMException('The featured image load was cancelled.', 'AbortError');
}
